/**
 * Artimli markdown - kararli onek / kuyruk ayrimi.
 *
 * ## Neden gerekli
 *
 * Her frame'de TUM birikmis metin uzerinde `marked.parse` + `DOMPurify` +
 * KaTeX + hljs kosmak frame basina O(n), yanit basina O(n^2) demek. 8 KB'da
 * ve 60 fps'de bu gorunur jank esigi.
 *
 * Cozum: markdown BLOK yapili. Son ust-seviye blok sinirindan oncesi, metin
 * eklendikce DEGISEMEZ. O yuzden:
 *   - kararli onek bir kez render edilip onbellege alinir,
 *   - yalnizca kuyruk her frame yeniden parse edilir (birkac yuz byte).
 *
 * ## FENCE PARITE KORUMASI: bu dosyadaki en kritik mantik
 *
 * Bos satir iceren bir ``` blogu O BOS SATIRLARDA BOLUNMEMELI. Bolunurse
 * bir kod blogu akis ortasinda paragraflara dagilir - kullanicinin gordugu
 * sey aniden bozulur ve sebebi anlasilmaz.
 *
 * Bu yuzden ayrim noktasi ararken fence PARITESI takip ediliyor: yalnizca
 * ACIK BIR FENCE ICINDE OLMAYAN bos satirlar sinir olabilir.
 *
 * Kapsanan durumlar (hepsi test edildi):
 *   - bos satirli fence
 *   - kapanmamis fence (akis ortasi)
 *   - liste icinde girintili nested fence
 *   - `~~~` fence
 *   - girintili fence
 *   - fence isareti icinde dil etiketi (```python)
 */

/** Bir satir fence acilisi/kapanisi mi? Degilse null. */
interface FenceMark {
  /** ``` veya ~~~ */
  char: '`' | '~'
  /** Isaret uzunlugu (en az 3). */
  length: number
  /** Satirin basindaki bosluk sayisi. */
  indent: number
}

const FENCE_RE = /^(\s*)(`{3,}|~{3,})(.*)$/

function fenceMark(line: string): FenceMark | null {
  const match = FENCE_RE.exec(line)
  if (!match) return null
  const [, spaces, marker] = match
  // 4+ bosluk girinti markdown'da KOD BLOGU demek, fence degil.
  if (spaces.length >= 4) return null
  return {
    char: marker[0] as '`' | '~',
    length: marker.length,
    indent: spaces.length,
  }
}

export interface SplitResult {
  /** Bir daha degismeyecek kisim. */
  stable: string
  /** Her frame yeniden render edilecek kisim. */
  tail: string
}

/**
 * Metni kararli onek ve kuyruk olarak ayirir.
 *
 * `from` verilirse taramaya oradan devam ediyor (onceki cagriden kalan
 * offset) - boylece toplam is O(n) kaliyor, O(n^2) degil.
 */
export function splitStable(text: string): SplitResult {
  // TEK GECIS. Iki seyi birlikte takip ediyoruz:
  //   - acik fence ICINDE OLMAYAN son bos-satir siniri
  //   - o an acik olan fence'ten ONCEKI son bos-satir siniri
  // Ikincisi gerekli: fence kapanmamissa fence'in kendisi ve sonrasi
  // kuyrukta kalmali, ama fence'ten oncesi kararli.
  let open: FenceMark | null = null
  let lastBoundary = 0
  let boundaryBeforeFence = 0
  let offset = 0

  for (const line of text.split('\n')) {
    const lineEnd = offset + line.length + 1
    const mark = fenceMark(line)

    if (open) {
      // Kapanis: AYNI karakter ve EN AZ ayni uzunlukta (markdown kurali).
      if (mark && mark.char === open.char && mark.length >= open.length) {
        open = null
      }
    } else if (mark) {
      // Fence aciliyor: bu noktaya kadarki son siniri hatirla.
      boundaryBeforeFence = lastBoundary
      open = mark
    } else if (line.trim() === '') {
      // ACIK FENCE ICINDE OLMAYAN bos satir: gecerli bir sinir.
      lastBoundary = lineEnd
    }

    offset = lineEnd
  }

  // Acik fence varsa sinir fence'ten ONCE olmak zorunda: yoksa kod blogu
  // bos satirlarindan bolunur ve paragraflara dagilir.
  const boundary = open ? boundaryBeforeFence : lastBoundary
  return { stable: text.slice(0, boundary), tail: text.slice(boundary) }
}

/**
 * Acik (kapanmamis) bir fence var mi?
 *
 * Arayuz bunu kullaniyor: kapanmamis fence icindeki kod HENUZ renklendirilmiyor
 * (hljs atlaniyor), cunku fence kapanana kadar dil bile bilinmiyor. Bu ayni
 * zamanda bugunku "yarim fence her frame yeniden renklendiriliyor"
 * titremesini de duzeltiyor.
 */
export function hasOpenFence(text: string): boolean {
  let open: FenceMark | null = null
  for (const line of text.split('\n')) {
    const mark = fenceMark(line)
    if (open) {
      if (mark && mark.char === open.char && mark.length >= open.length) open = null
    } else if (mark) {
      open = mark
    }
  }
  return open !== null
}
